import { useState, useCallback, useContext } from 'react';
import isNil from 'lodash/isNil';
import partition from 'lodash/partition';
import { isBackendAPIError, performGetRequest } from '@atlassian/jira-fetch/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import Ipcontext from '../context/index.tsx';
import { SERVICES_ERROR_ID, ERROR_REPORTING_TEAM, PACKAGE_NAME } from '../../common/constants.tsx';
import type { ScopeFilterFieldType } from './types.tsx';

export const useFetchScopeFilters = () => {
	const { planId } = useContext(Ipcontext);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [planCustomFields, setPlanCustomFields] = useState<ScopeFilterFieldType[]>([]);
	const [externalCustomFields, setExternalCustomFields] = useState<ScopeFilterFieldType[]>([]);

	const fetchScopeFilters = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);

			if (isNil(planId)) {
				throw new Error('planId is empty in the fetch all custom fields request');
			}

			const values = await performGetRequest<ScopeFilterFieldType[] | undefined>(
				`/rest/jpo/2.0/plans/${planId}/increments/scopeFilterFields`,
			);

			const [includedInPlan, notIncludedInPlan] = partition(values, 'includedInPlan');

			setPlanCustomFields(includedInPlan || []);
			setExternalCustomFields(notIncludedInPlan || []);
		} catch (e: unknown) {
			const errorFormatted = e instanceof Error ? e : new Error(String(e));
			fireErrorAnalytics({
				meta: {
					id: SERVICES_ERROR_ID,
					packageName: PACKAGE_NAME,
					teamName: ERROR_REPORTING_TEAM,
				},
				attributes: {
					serviceName: 'useFetchScopeFilters',
					skipSentry: isBackendAPIError(e),
				},
				error: errorFormatted,
				sendToPrivacyUnsafeSplunk: true,
			});
			setError(errorFormatted);
			setPlanCustomFields([]);
			setExternalCustomFields([]);
			return [];
		} finally {
			setIsLoading(false);
		}
	}, [setError, planId]);

	return {
		isLoading,
		error,
		planCustomFields,
		externalCustomFields,
		fetchScopeFilters,
	};
};
